import {
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonSpinner,
  IonToolbar,
  useIonRouter,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { calendarOutline, closeOutline, warningOutline } from "ionicons/icons";
import { toast } from "sonner";
import { ProjectImage } from "@/components/molecules/project/project-image";
import { api } from "@/lib/backend";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { useFeatureFlags } from "@/lib/backend/utils/hooks/use-feature-flags";
import { Routes } from "@/lib/router";
import { formatInTz, formatTimeRange } from "@/utils/format-datetime";

export function TransferClaimPage() {
  const { t } = useTranslation();
  const router = useIonRouter();
  const { token } = useParams<{ token: string }>();

  // Check if shift swap feature is enabled
  const { data: featureFlags, isLoading: featureFlagsLoading } = useFeatureFlags();
  const shiftSwapEnabled = featureFlags?.shiftSwapEnabled ?? false;

  // Fetch transfer details (only if feature is enabled)
  const {
    data: transferDetails,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["transfer-details", token],
    queryFn: () => extractionWrapper(api.shiftCancellation.getTransferDetails(token)),
    enabled: !!token && shiftSwapEnabled,
    retry: false,
  });

  // Mutation to claim the transfer
  const claimMutation = useMutation({
    mutationFn: () => extractionWrapper(api.shiftCancellation.acceptTransfer(token)),
    onSuccess: () => {
      toast.success(t("shift:transfer.claim.success"));
      router.push(Routes.UpcomingShifts(), "root", "replace");
    },
    onError: (error: any) => {
      const errorCode =
        error?.error?.errorCode || error?.data?.errorCode || error?.errorCode || null;
      const errorMessage = errorCode ? t(`error-codes:${errorCode}`) : undefined;
      toast.error(errorMessage || t("shift:transfer.claim.error.generic"));
    },
  });

  const handleClose = () => {
    if (router.canGoBack()) {
      router.goBack();
    } else {
      router.push(Routes.UpcomingShifts(), "root", "replace");
    }
  };

  const handleClaim = () => {
    claimMutation.mutate();
  };

  // Format date and time — in projecttijd, niet in die van het toestel. Een overgenomen dienst van
  // 15:00-23:00 las op een telefoon in Toronto als 09:00-17:00. `TransferShiftDetails` draagt geen
  // zone, dus valt dit terug op Amsterdam.
  const formatDateTime = (startAt: string, endAt: string) => {
    const dateStr = formatInTz(startAt, { day: "numeric", month: "long", year: "numeric" });

    return `${dateStr}, ${formatTimeRange(startAt, endAt)}`;
  };

  // Determine error message
  const getErrorMessage = () => {
    if (transferDetails?.claimed) {
      return t("shift:transfer.claim.error.already-claimed");
    }
    if (transferDetails?.error?.toLowerCase().includes("expired")) {
      return t("shift:transfer.claim.error.expired");
    }
    if (transferDetails?.valid === false) {
      return transferDetails?.error || t("shift:transfer.claim.error.invalid");
    }
    if (error) {
      return t("shift:transfer.claim.error.not-found");
    }
    return null;
  };

  const errorMessage = getErrorMessage();
  const isValid = transferDetails?.valid && !transferDetails?.claimed && !error;
  const shift = transferDetails?.shift;

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar className="bg-surface-primary">
          <div className="flex items-center justify-between px-6 py-3">
            <h1 className="text-xl font-bold text-txt-primary">
              {t("shift:transfer.claim.title")}
            </h1>
            <button
              type="button"
              onClick={handleClose}
              aria-label={t("common:a11y.close")}
              className="touch-target flex size-8 items-center justify-center"
            >
              <IonIcon icon={closeOutline} className="text-2xl text-txt-tertiary" />
            </button>
          </div>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        {/* Loading state */}
        {(isLoading || featureFlagsLoading) && (
          <div className="flex h-full flex-col items-center justify-center gap-4">
            <IonSpinner className="size-10" />
            <p className="text-md text-txt-secondary">{t("shift:transfer.claim.loading")}</p>
          </div>
        )}

        {/* Feature disabled state */}
        {!featureFlagsLoading && !shiftSwapEnabled && (
          <div className="flex h-full flex-col items-center justify-center gap-6 px-6">
            <div className="flex size-20 items-center justify-center rounded-full bg-error-10">
              {/* Standalone 40px glyph inside its own tinted circle, with the message rendered as
                  real text below it. WCAG 1.4.11 asks 3:1 for a graphic and `error-100` clears
                  that on `error-10` for every brand, so this keeps the flat brand red. */}
              {/* eslint-disable-next-line no-restricted-syntax */}
              <IonIcon icon={warningOutline} className="size-10 text-error-100" />
            </div>
            <div className="flex flex-col items-center gap-2 text-center">
              <h2 className="text-xl font-bold text-txt-primary">
                {t("shift:transfer.claim.error.title")}
              </h2>
              <p className="text-md text-txt-secondary">
                {t("shift:transfer.claim.error.feature-disabled")}
              </p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="mt-4 rounded-xl bg-surface-tertiary px-6 py-3 text-base font-semibold text-txt-primary"
            >
              {t("common:close")}
            </button>
          </div>
        )}

        {/* Error state */}
        {!isLoading && !featureFlagsLoading && shiftSwapEnabled && errorMessage && (
          <div className="flex h-full flex-col items-center justify-center gap-6 px-6">
            <div className="flex size-20 items-center justify-center rounded-full bg-error-10">
              {/* Standalone 40px glyph inside its own tinted circle, with the message rendered as
                  real text below it. WCAG 1.4.11 asks 3:1 for a graphic and `error-100` clears
                  that on `error-10` for every brand, so this keeps the flat brand red. */}
              {/* eslint-disable-next-line no-restricted-syntax */}
              <IonIcon icon={warningOutline} className="size-10 text-error-100" />
            </div>
            <div className="flex flex-col items-center gap-2 text-center">
              <h2 className="text-xl font-bold text-txt-primary">
                {t("shift:transfer.claim.error.title")}
              </h2>
              <p className="text-md text-txt-secondary">{errorMessage}</p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="mt-4 rounded-xl bg-surface-tertiary px-6 py-3 text-base font-semibold text-txt-primary"
            >
              {t("common:close")}
            </button>
          </div>
        )}

        {/* Success state - show shift details */}
        {!isLoading && !featureFlagsLoading && shiftSwapEnabled && isValid && shift && (
          <div className="flex h-full flex-col">
            <div className="flex flex-1 flex-col items-center gap-6 px-6 pt-4">
              {/* Project image */}
              <ProjectImage
                projectId={shift.projectId}
                imageKey={shift.projectImage}
                alt={shift.projectName || "Project"}
                className="aspect-[16/9] w-full rounded-xl"
              />

              {/* Project name */}
              <h2 className="text-center text-2xl font-bold text-txt-primary">
                {shift.projectName}
              </h2>

              {/* Shift details */}
              <div className="flex flex-col items-center gap-1 text-center">
                <p className="text-md text-txt-secondary">
                  {formatDateTime(shift.startAt, shift.endAt)}
                </p>
                {shift.positionName && (
                  <p className="text-md text-txt-secondary">{shift.positionName}</p>
                )}
                {shift.functionName && (
                  <p className="text-md font-medium text-brand-500">{shift.functionName}</p>
                )}
              </div>

              {/* Offered by */}
              {transferDetails?.offeredBy && (
                <p className="text-sm text-txt-tertiary">
                  {t("shift:transfer.claim.offered-by")} {transferDetails.offeredBy}
                </p>
              )}
            </div>

            {/* Claim button - fixed at bottom */}
            <div className="px-6 pb-8 pt-4">
              <button
                type="button"
                onClick={handleClaim}
                disabled={claimMutation.isPending}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-solid border-bordercolor-primary bg-surface-primary px-4 py-4 transition-colors active:bg-surface-secondary disabled:opacity-50"
              >
                <IonIcon icon={calendarOutline} className="text-xl text-brand-500" />
                <span className="text-base font-semibold text-brand-500">
                  {claimMutation.isPending
                    ? t("shift:transfer.claim.claiming")
                    : t("shift:transfer.claim.button")}
                </span>
              </button>
            </div>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
}
